import { useMutation, useQueryClient } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"

import {
  completeJob,
  holdJob,
  reopenJob,
  resumeJob,
  startJob,
} from "src/api/Service/Jobs"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { JOB_STATUS_TRANSITIONED } from "../amplitude_events"

const useJobTransition = ({ jobId, workOrderSequentialId, currentStatus }) => {
  const { marinaSlug } = useMarina()
  const showToast = useToast()
  const queryClient = useQueryClient()
  const tracker = useTracker()

  const trackTransition = (to, extra = {}) =>
    tracker.trackEvent(JOB_STATUS_TRANSITIONED, {
      from: currentStatus,
      to,
      ...extra,
    })

  const invalidate = () => {
    queryClient.invalidateQueries([
      "workOrder",
      marinaSlug,
      workOrderSequentialId,
    ])
    queryClient.invalidateQueries(["jobActivity", marinaSlug, jobId])
  }

  const handleError = (action) => (error) => {
    const message =
      error?.response?.data?.errors?.[0] ||
      error?.message ||
      `Could not ${action} this job.`
    showToast(message, { type: "error" })
  }

  const startMutation = useMutation(
    () => startJob(marinaSlug, workOrderSequentialId, jobId),
    {
      onSuccess: () => {
        invalidate()
        trackTransition("in_progress")
      },
      onError: handleError("start"),
    }
  )

  const completeMutation = useMutation(
    () => completeJob(marinaSlug, workOrderSequentialId, jobId),
    {
      onSuccess: () => {
        invalidate()
        trackTransition("work_complete")
      },
      onError: handleError("complete"),
    }
  )

  const reopenMutation = useMutation(
    () => reopenJob(marinaSlug, workOrderSequentialId, jobId),
    {
      onSuccess: () => {
        invalidate()
        queryClient.invalidateQueries(["jobLineItems", marinaSlug, jobId])
        queryClient.invalidateQueries([
          "workOrderInvoices",
          marinaSlug,
          workOrderSequentialId,
        ])
        showToast("Job reopened.", { type: "success" })
        trackTransition("open")
      },
      onError: handleError("reopen"),
    }
  )

  const holdMutation = useMutation(
    ({ holdReason }) =>
      holdJob(marinaSlug, workOrderSequentialId, jobId, holdReason),
    {
      onSuccess: (_data, { holdReason }) => {
        invalidate()
        showToast("Job placed on hold.", { type: "success" })
        trackTransition("on_hold", { hold_reason: holdReason })
      },
      onError: handleError("put on hold"),
    }
  )

  const resumeMutation = useMutation(
    () => resumeJob(marinaSlug, workOrderSequentialId, jobId),
    {
      onSuccess: () => {
        invalidate()
        trackTransition("in_progress")
      },
      onError: handleError("resume"),
    }
  )

  return {
    start: () => startMutation.mutate(),
    complete: () => completeMutation.mutate(),
    reopen: (options = {}) => reopenMutation.mutate(undefined, options),
    hold: (holdReason, options = {}) =>
      holdMutation.mutate({ holdReason }, options),
    resume: () => resumeMutation.mutate(),
    isTransitioning:
      startMutation.isLoading ||
      completeMutation.isLoading ||
      reopenMutation.isLoading ||
      holdMutation.isLoading ||
      resumeMutation.isLoading,
  }
}

export default useJobTransition
